import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileUploader } from "@/components/ui/file-uploader";
import { useState, useRef } from "react";

interface VideoDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  currentVideoUrl?: string;
  title: string;
  maxSize?: number; // Size in MB
}

export function VideoDialog({
  open,
  onClose,
  onUpload,
  currentVideoUrl,
  title,
  maxSize = 100, // Default to 100MB
}: VideoDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      await onUpload(selectedFile);
      onClose();
    } catch (error) {
      console.error("Error uploading profile video:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Choose a new video to upload. Supported formats: MP4, QuickTime,
            WebM. Maximum size: {maxSize}MB
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-6 py-4">
          {(previewUrl || currentVideoUrl) && (
            <video
              ref={videoRef}
              src={previewUrl || currentVideoUrl}
              className="max-h-[300px] w-full rounded-lg object-contain"
              controls
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
          )}
          <FileUploader
            onFileSelect={handleFileSelect}
            accept="video/mp4,video/quicktime,video/x-m4v,video/webm,video/mov"
            maxSize={maxSize * 1024 * 1024} // Convert MB to bytes
            buttonText={
              selectedFile ? "Choose Different Video" : "Choose Video"
            }
            variant="outline"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
